/**
 * IT training (8 Oct): lessons for IT staff on using the ITSM and the IT
 * HelpDesk, kept as one Markdown file per lesson.
 *
 * WHY FILES, NOT A TABLE
 * ----------------------
 * The content is ours, changes with the process, and is reviewed like code.
 * A lesson is `content/<lang>/<slug>.md`: a small frontmatter block, then
 * Markdown with four known sections. Adding one is adding a file — the index,
 * the topic groups, the search and the lesson page all come from here.
 *
 * Kept import-free so `node --test` can load it straight from the source
 * (`tests/it-training.test.mjs`); Vite's `import.meta.glob` lives in
 * `../content.ts`, which hands the raw files to {@link buildLessonIndex}.
 *
 * FILE FORMAT (the "What's new" list is `content/<lang>/_whats-new.md`, see
 * {@link parseWhatsNew}):
 *
 *     ---
 *     title: Resolve, don't close
 *     summary: Set Resolved with a fix note and let Ivanti close it.
 *     topic: tickets            # tickets | routing | access | tools
 *     order: 1                  # within the topic, lowest first
 *     updated: 2026-10-08       # YYYY-MM-DD
 *     status: published         # published | draft (optional, default published)
 *     keywords: close, status   # optional, comma-separated, for the search
 *     admin-note: text          # optional, shown to HelpDesk admins only
 *     ---
 *     ## When to use it
 *     ## Steps
 *     ### <one step's title>      (each becomes a numbered step card)
 *     Ivanti: <where to click>    (a "where" row; also `HelpDesk:`)
 *     ![alt text](TODO)           (a screenshot slot; TODO = placeholder)
 *     > [!TIP] text               (a callout: TIP, WARNING, CHAT, NOTE)
 *     {{sla-matrix}}              (a built-in picture, see LESSON_WIDGETS)
 *     ## Common mistakes
 *     ## Why it matters
 *
 * Nothing here produces HTML: blocks are data, rendered as React elements by
 * the lesson page (Markdown text through the app's safe `<Markdown>`).
 */

/** The topic groups, in the order the page shows them. */
export const TRAINING_TOPICS = ["tickets", "routing", "access", "tools"] as const;
export type TrainingTopic = (typeof TRAINING_TOPICS)[number];

/** The language every lesson is written in first, and falls back to. */
export const SOURCE_LANGUAGE = "en";

export type LessonStatus = "published" | "draft";

/**
 * Pictures drawn by the page from data, placed by a line `{{name}}` on its
 * own. The SLA ones read `./sla-reference.ts` (16 Oct). An unknown name stays
 * text, so a typo shows up on the page instead of vanishing.
 */
export const LESSON_WIDGETS = ["sla-matrix", "sla-targets", "sla-escalation"] as const;
export type LessonWidget = (typeof LESSON_WIDGETS)[number];

export interface LessonMeta {
  slug: string;
  /** The language of the file this came from (may differ from the reader's). */
  lang: string;
  title: string;
  /** "In one line". */
  summary: string;
  topic: TrainingTopic;
  order: number;
  /** YYYY-MM-DD. */
  updated: string;
  status: LessonStatus;
  keywords: string[];
  /** For HelpDesk admins only (an open question on the content); null when none. */
  adminNote: string | null;
}

export type CalloutTone = "tip" | "warning" | "chat" | "note";
export type WherePlace = "ivanti" | "helpdesk";

export type LessonBlock =
  | { kind: "markdown"; text: string }
  /** `src` null: a placeholder ("screenshot to come"), alt text shown. */
  | { kind: "image"; alt: string; src: string | null }
  | { kind: "callout"; tone: CalloutTone; text: string }
  | { kind: "where"; place: WherePlace; text: string }
  | { kind: "widget"; name: LessonWidget };

export interface LessonStep {
  title: string;
  blocks: LessonBlock[];
}

export type LessonSectionId = "when" | "steps" | "mistakes" | "why" | "other";

export interface LessonSection {
  id: LessonSectionId;
  /** As written in the file; empty for text before the first `##`. */
  heading: string;
  blocks: LessonBlock[];
  /** Only in the `steps` section: each `###` subsection. */
  steps: LessonStep[];
}

export interface Lesson extends LessonMeta {
  body: string;
  sections: LessonSection[];
}

export interface WhatsNewEntry {
  /** YYYY-MM-DD. */
  date: string;
  slug: string;
  text: string;
}

export interface LessonIndex {
  /** lang → slug → lesson. */
  lessons: Map<string, Map<string, Lesson>>;
  /** lang → entries, newest first. */
  whatsNew: Map<string, WhatsNewEntry[]>;
}

/** A lesson as one reader gets it: their language, or English. */
export interface LessonEntry {
  lesson: Lesson;
  /** True when the reader's language has no version and English is shown. */
  fallback: boolean;
}

export class LessonFormatError extends Error {
  constructor(file: string, problem: string) {
    super(`IT training: ${file}: ${problem}`);
    this.name = "LessonFormatError";
  }
}

// ─── Frontmatter ─────────────────────────────────────────────────────────

const FRONTMATTER = /^---[ \t]*\r?\n([\s\S]*?)\r?\n---[ \t]*(?:\r?\n|$)/;
const DATE = /^\d{4}-\d{2}-\d{2}$/;
const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

/** `key: value` lines; `#` comments and blank lines ignored; quotes trimmed. */
export function parseFrontmatter(raw: string): { data: Record<string, string>; body: string } {
  const match = FRONTMATTER.exec(raw.replace(/^\uFEFF/, ""));
  if (match === null) {
    return { data: {}, body: raw };
  }
  const data: Record<string, string> = {};
  for (const line of (match[1] ?? "").split(/\r?\n/)) {
    const trimmed = line.trim();
    if (trimmed === "" || trimmed.startsWith("#")) {
      continue;
    }
    const colon = trimmed.indexOf(":");
    if (colon <= 0) {
      continue;
    }
    const key = trimmed.slice(0, colon).trim();
    let value = trimmed.slice(colon + 1).trim();
    // A trailing comment, but never inside a quoted value.
    if (!/^["']/.test(value)) {
      value = value.replace(/\s+#.*$/, "");
    }
    value = value.replace(/^(["'])(.*)\1$/, "$2");
    data[key] = value;
  }
  return { data, body: raw.replace(/^\uFEFF/, "").slice(match[0].length) };
}

function isTopic(value: string): value is TrainingTopic {
  return (TRAINING_TOPICS as readonly string[]).includes(value);
}

function isWidget(value: string): value is LessonWidget {
  return (LESSON_WIDGETS as readonly string[]).includes(value);
}

// ─── Body ────────────────────────────────────────────────────────────────

const SECTION_HEADING = /^##\s+(.+?)(?:\s*\{#([a-z]+)\})?\s*$/;
const STEP_HEADING = /^###\s+(.+?)\s*$/;
const IMAGE_LINE = /^!\[([^\]]*)\]\(([^)\s]*)\)\s*$/;
const CALLOUT_START = /^>\s*\[!(tip|warning|chat|note)\]\s*(.*)$/i;
const QUOTE_LINE = /^>\s?(.*)$/;
const WHERE_LINE = /^(Ivanti|HelpDesk):\s+(.+)$/;
const WIDGET_LINE = /^\{\{\s*([a-z-]+)\s*\}\}$/;
/** Screenshots are ours, served from `public/`: a plain same-origin path. */
const SAFE_IMAGE_SRC = /^\/[A-Za-z0-9/_-]+\.(?:png|jpe?g|webp|gif)$/;

/** English headings → section ids. Another language uses `{#id}`. */
const SECTION_NAMES: Readonly<Record<string, LessonSectionId>> = {
  "when to use it": "when",
  steps: "steps",
  "common mistakes": "mistakes",
  "why it matters": "why",
};
const SECTION_IDS: readonly LessonSectionId[] = ["when", "steps", "mistakes", "why", "other"];

function sectionId(heading: string, explicit: string | undefined): LessonSectionId {
  if (explicit !== undefined && (SECTION_IDS as readonly string[]).includes(explicit)) {
    return explicit as LessonSectionId;
  }
  return SECTION_NAMES[heading.trim().toLowerCase()] ?? "other";
}

/** `TODO`, empty or anything but a plain local path → a placeholder. */
export function safeImageSrc(raw: string): string | null {
  const src = raw.trim();
  return SAFE_IMAGE_SRC.test(src) && !src.includes("..") ? src : null;
}

/** Lines of one section or step → blocks. */
export function parseBlocks(lines: readonly string[]): LessonBlock[] {
  const blocks: LessonBlock[] = [];
  let text: string[] = [];
  const flush = () => {
    const joined = text.join("\n").trim();
    if (joined !== "") {
      blocks.push({ kind: "markdown", text: joined });
    }
    text = [];
  };

  for (let index = 0; index < lines.length; index += 1) {
    const line = lines[index] ?? "";
    const trimmed = line.trim();

    const image = IMAGE_LINE.exec(trimmed);
    if (image !== null) {
      flush();
      blocks.push({
        kind: "image",
        alt: (image[1] ?? "").trim(),
        src: safeImageSrc(image[2] ?? ""),
      });
      continue;
    }

    const widget = WIDGET_LINE.exec(trimmed);
    if (widget !== null && isWidget(widget[1] ?? "")) {
      flush();
      blocks.push({ kind: "widget", name: widget[1] as LessonWidget });
      continue;
    }

    const where = WHERE_LINE.exec(trimmed);
    if (where !== null) {
      flush();
      blocks.push({
        kind: "where",
        place: where[1] === "Ivanti" ? "ivanti" : "helpdesk",
        text: (where[2] ?? "").trim(),
      });
      continue;
    }

    const callout = CALLOUT_START.exec(trimmed);
    if (callout !== null || QUOTE_LINE.test(trimmed)) {
      flush();
      const tone = (callout?.[1]?.toLowerCase() ?? "note") as CalloutTone;
      const quoted: string[] = [];
      if (callout !== null) {
        quoted.push(callout[2] ?? "");
      } else {
        quoted.push(QUOTE_LINE.exec(trimmed)?.[1] ?? "");
      }
      while (index + 1 < lines.length && QUOTE_LINE.test((lines[index + 1] ?? "").trim())) {
        index += 1;
        quoted.push(QUOTE_LINE.exec((lines[index] ?? "").trim())?.[1] ?? "");
      }
      const body = quoted.join("\n").trim();
      if (body !== "") {
        blocks.push({ kind: "callout", tone, text: body });
      }
      continue;
    }

    text.push(line);
  }
  flush();
  return blocks;
}

/** The body → sections; `###` inside "Steps" → numbered steps. */
export function parseSections(body: string): LessonSection[] {
  const sections: LessonSection[] = [];
  let current: { id: LessonSectionId; heading: string; lines: string[] } = {
    id: "other",
    heading: "",
    lines: [],
  };
  const raw: (typeof current)[] = [current];

  for (const line of body.replace(/\r\n/g, "\n").split("\n")) {
    const heading = SECTION_HEADING.exec(line);
    if (heading !== null) {
      current = {
        id: sectionId(heading[1] ?? "", heading[2]),
        heading: heading[1] ?? "",
        lines: [],
      };
      raw.push(current);
      continue;
    }
    current.lines.push(line);
  }

  for (const part of raw) {
    if (part.id !== "steps") {
      const blocks = parseBlocks(part.lines);
      if (blocks.length > 0 || part.heading !== "") {
        sections.push({ id: part.id, heading: part.heading, blocks, steps: [] });
      }
      continue;
    }
    const lead: string[] = [];
    const steps: { title: string; lines: string[] }[] = [];
    for (const line of part.lines) {
      const step = STEP_HEADING.exec(line);
      if (step !== null) {
        steps.push({ title: step[1] ?? "", lines: [] });
      } else if (steps.length > 0) {
        steps[steps.length - 1]?.lines.push(line);
      } else {
        lead.push(line);
      }
    }
    sections.push({
      id: "steps",
      heading: part.heading,
      blocks: parseBlocks(lead),
      steps: steps.map((step) => ({ title: step.title, blocks: parseBlocks(step.lines) })),
    });
  }
  return sections;
}

// ─── One file ────────────────────────────────────────────────────────────

/** One lesson file → a lesson. Throws {@link LessonFormatError} when it is not one. */
export function parseLesson(file: string, slug: string, lang: string, raw: string): Lesson {
  if (!SLUG.test(slug)) {
    throw new LessonFormatError(file, `"${slug}" is not a slug (a-z, 0-9 and dashes)`);
  }
  const { data, body } = parseFrontmatter(raw);
  const title = data.title?.trim() ?? "";
  const summary = data.summary?.trim() ?? "";
  const topic = data.topic?.trim() ?? "";
  const updated = data.updated?.trim() ?? "";
  const status = (data.status?.trim() || "published") as LessonStatus;
  if (title === "") {
    throw new LessonFormatError(file, "frontmatter needs a title");
  }
  if (summary === "") {
    throw new LessonFormatError(file, "frontmatter needs a summary (the one line)");
  }
  if (!isTopic(topic)) {
    throw new LessonFormatError(file, `topic must be one of ${TRAINING_TOPICS.join(", ")}`);
  }
  if (!DATE.test(updated) || Number.isNaN(Date.parse(updated))) {
    throw new LessonFormatError(file, "updated must be a date, YYYY-MM-DD");
  }
  if (status !== "published" && status !== "draft") {
    throw new LessonFormatError(file, "status must be published or draft");
  }
  const order = Number.parseInt(data.order ?? "", 10);
  const keywords = (data.keywords ?? "")
    .split(",")
    .map((word) => word.trim())
    .filter((word) => word !== "");
  return {
    slug,
    lang,
    title,
    summary,
    topic,
    order: Number.isFinite(order) ? order : 100,
    updated,
    status,
    keywords,
    adminNote: data["admin-note"]?.trim() || null,
    body,
    sections: parseSections(body),
  };
}

const WHATS_NEW_LINE =
  /^\s*[-*]\s+\*\*(\d{4}-\d{2}-\d{2})\*\*\s*[:·|—–-]?\s*\[([^\]]+)\]\(([a-z0-9-]+)\)\s*$/;

/**
 * `_whats-new.md`: one bullet per change, newest anywhere (sorted here).
 *
 *     - **2026-10-08** · [Waiting on customer changed — lesson coming](waiting-on-customer)
 *
 * Other lines (a heading, a comment) are ignored.
 */
export function parseWhatsNew(raw: string): WhatsNewEntry[] {
  const entries: WhatsNewEntry[] = [];
  for (const line of parseFrontmatter(raw).body.split(/\r?\n/)) {
    const match = WHATS_NEW_LINE.exec(line);
    if (match !== null) {
      entries.push({ date: match[1] ?? "", text: (match[2] ?? "").trim(), slug: match[3] ?? "" });
    }
  }
  // Stable: same-day entries keep the file's order.
  return entries
    .map((entry, position) => ({ entry, position }))
    .sort((a, b) => b.entry.date.localeCompare(a.entry.date) || a.position - b.position)
    .map(({ entry }) => entry);
}

// ─── The index ───────────────────────────────────────────────────────────

const CONTENT_PATH = /(?:^|\/)([A-Za-z]{2,3}(?:-[A-Za-z0-9]{2,4})?)\/([^/]+)\.md$/;

/**
 * Every content file (path → raw text) → the index.
 *
 * Paths end in `<lang>/<name>.md`. `_whats-new.md` is the change list; any
 * other name starting with `_` is ignored, the rest are lessons.
 */
export function buildLessonIndex(files: Readonly<Record<string, string>>): LessonIndex {
  const lessons = new Map<string, Map<string, Lesson>>();
  const whatsNew = new Map<string, WhatsNewEntry[]>();
  for (const [path, raw] of Object.entries(files)) {
    const match = CONTENT_PATH.exec(path);
    if (match === null) {
      continue;
    }
    const lang = match[1] ?? "";
    const name = match[2] ?? "";
    if (name === "_whats-new") {
      whatsNew.set(lang, parseWhatsNew(raw));
      continue;
    }
    if (name.startsWith("_")) {
      continue;
    }
    const lesson = parseLesson(path, name, lang, raw);
    let byLang = lessons.get(lang);
    if (byLang === undefined) {
      byLang = new Map();
      lessons.set(lang, byLang);
    }
    byLang.set(name, lesson);
  }
  return { lessons, whatsNew };
}

/** The reader's language, then its base (`pt-BR` → `pt`), then English. */
function languageChain(lang: string): string[] {
  const chain = [lang];
  const base = lang.split("-")[0] ?? lang;
  if (base !== lang) {
    chain.push(base);
  }
  if (!chain.includes(SOURCE_LANGUAGE)) {
    chain.push(SOURCE_LANGUAGE);
  }
  return chain;
}

/** One lesson in the reader's language, else English; null when unknown. */
export function findLesson(index: LessonIndex, slug: string, lang: string): LessonEntry | null {
  for (const candidate of languageChain(lang)) {
    const lesson = index.lessons.get(candidate)?.get(slug);
    if (lesson !== undefined) {
      return { lesson, fallback: candidate !== lang && lang !== SOURCE_LANGUAGE };
    }
  }
  return null;
}

/** Topic order, then `order`, then title. */
function compareLessons(a: Lesson, b: Lesson): number {
  return (
    TRAINING_TOPICS.indexOf(a.topic) - TRAINING_TOPICS.indexOf(b.topic) ||
    a.order - b.order ||
    a.title.localeCompare(b.title)
  );
}

/**
 * Every lesson for a reader. English is the master list: a lesson exists when
 * its English file does; a translation only replaces the words.
 */
export function listLessons(index: LessonIndex, lang: string): LessonEntry[] {
  const slugs = [...(index.lessons.get(SOURCE_LANGUAGE)?.keys() ?? [])];
  return slugs
    .map((slug) => findLesson(index, slug, lang))
    .filter((entry): entry is LessonEntry => entry !== null)
    .sort((a, b) => compareLessons(a.lesson, b.lesson));
}

/** The change list for a reader (their language, else English), newest first. */
export function whatsNewFor(index: LessonIndex, lang: string): WhatsNewEntry[] {
  for (const candidate of languageChain(lang)) {
    const entries = index.whatsNew.get(candidate);
    if (entries !== undefined) {
      return entries;
    }
  }
  return [];
}

/** Lessons grouped by topic, in topic order; empty topics dropped. */
export function groupByTopic(
  entries: readonly LessonEntry[],
): { topic: TrainingTopic; entries: LessonEntry[] }[] {
  return TRAINING_TOPICS.map((topic) => ({
    topic,
    entries: entries.filter((entry) => entry.lesson.topic === topic),
  })).filter((group) => group.entries.length > 0);
}

// ─── Search ──────────────────────────────────────────────────────────────

/** Lower case, accents off, so "resolu" finds "Résolu" and "RESOLVED". */
export function normaliseSearch(text: string): string {
  return text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase().replace(/\s+/g, " ").trim();
}

/**
 * Lessons whose title, one line, keywords, topic name or text contain every
 * word typed. An empty query keeps them all.
 */
export function filterLessons(
  entries: readonly LessonEntry[],
  query: string,
  topicLabel: (topic: TrainingTopic) => string = (topic) => topic,
): LessonEntry[] {
  const words = normaliseSearch(query)
    .split(" ")
    .filter((word) => word !== "");
  if (words.length === 0) {
    return [...entries];
  }
  return entries.filter(({ lesson }) => {
    const haystack = normaliseSearch(
      [
        lesson.title,
        lesson.summary,
        lesson.keywords.join(" "),
        topicLabel(lesson.topic),
        lesson.body,
      ].join(" "),
    );
    return words.every((word) => haystack.includes(word));
  });
}
